import { keepPreviousData } from "@tanstack/react-query";
import { RealAssetDeploymentResponse } from "../apiClient";
import { convertTimestampToSeconds } from "../utils";
import { getEnv } from "../utils/env";

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
export const getUpstreamEndpoint = () =>
  getEnv().NEXT_PUBLIC_CLIENT_SIDE_API_URL;
export const poll = async function (fn: () => Promise<any>, fnCondition: (result: any) => boolean, interval: number) {
  let result = await fn();
  while (fnCondition(result)) {
    await sleep(interval);
    result = await fn();
  }
  return result;
};

export const paginatedApiToBatchGenerator = ({
  fn,
  args,
  downloadPageSize = 100,
  sizeKey = "size",
  dataKey = "items",
  pageKey = "page",
}: {
  fn: (params: any) => Promise<any>;
  args: Record<string, any>;
  downloadPageSize?: number;
  sizeKey?: string;
  dataKey?: string;
  pageKey?: string;
}) => {
  return async function* generator() {
    let page = 1;
    let hasMore = true;
    while (hasMore) {
      const resp = await fn({
        ...args,
        [pageKey]: page,
        [sizeKey]: downloadPageSize,
      });
      if (resp[dataKey].length < downloadPageSize) {
        hasMore = false;
      }
      page += 1;
      yield resp[dataKey];
    }
  };
};


export async function getDataFromAllPages(args: Parameters<typeof paginatedApiToBatchGenerator>[0]) {
    const generator = paginatedApiToBatchGenerator(args);
    const results = [];
    for await (const batch of generator()) {
      results.push(...batch);
    }
    return results;
}

export function isPaginatedResponse<T>(response: any): response is T {
  return response && Array.isArray(response.items);
}

export const renderDeploymentName = (
  d: Pick<RealAssetDeploymentResponse, "external_id" | "name">
) => (d.name && d.name !== d.external_id ? `${d.name} (${d.external_id})` : d.external_id);

export const getRealAssetRecordForExactNameMatch = async (api: any, name: string) => {
  return (await api.dataAccess.listRealAssets({ query: name })).items.find(
    (r: any) => r.name === name
  );
};

// Settings to use for React Query hooks where we only need to load the data once,
// and do not need to revalidate/refresh that data during a given session.
// Note: throwOnError is also set globally in main.tsx; explicit here for clarity.
export const QUERY_OPTIONS_LOAD_ONCE = {
  throwOnError: true,
  placeholderData: keepPreviousData,
  refetchOnWindowFocus: false,
  staleTime: Number.MAX_SAFE_INTEGER
}


export const getThumbnailUrlForMakoImages = (image: { url: string }) => {
  const { url } = image;
  const videoFrameTimestamp = url.match(/#t=(.+$)/)?.[1];
  const pathSegments = new URL(url).pathname.split("/");
  const index = pathSegments.indexOf("data_asset");

  if (index === -1 || index + 1 >= pathSegments.length) {
    return null;
  }

  const publicId = pathSegments[index + 1];
  let thumbnailUrl = `${getEnv().NEXT_PUBLIC_CLIENT_SIDE_API_URL}/data_asset/${publicId}/thumbnail`;

  // If there is a timestamp set, use the thumbnail endpoint with timestamp
  if (videoFrameTimestamp) {
    const timestampSeconds = convertTimestampToSeconds(videoFrameTimestamp);
    thumbnailUrl += `?timestamp=${timestampSeconds}`;
  }

  return thumbnailUrl;
};
